import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, Save, BadgeCheck, Heart, Users, MapPin, ExternalLink, Zap, AlertTriangle, HelpCircle, Info, Lock } from "lucide-react";
import { PanelBadge } from "@/components/panel/PanelBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { dbErrorMessage } from "@/lib/dbErrorMessage";

const HelpHint = ({ children, label = "Pomoc" }: { children: React.ReactNode; label?: string }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" align="start" className="w-72 text-xs leading-relaxed space-y-2">
      {children}
    </PopoverContent>
  </Popover>
);

const fetchShelter = async (slug: string) => {
  const { data, error } = await supabase.from("shelters").select("*").eq("slug", slug).single();
  if (error) throw error;
  return data;
};

const EditNeeds = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: shelter, isLoading } = useQuery({
    queryKey: ["panel-shelter", slug],
    enabled: !!slug,
    queryFn: () => fetchShelter(slug!),
  });

  const [form, setForm] = useState<any>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const markTouched = (key: string) =>
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  useEffect(() => {
    if (shelter) {
      setForm({
        current_needs: shelter.current_needs ?? "",
        urgent_needs: !!shelter.urgent_needs,
        volunteer_info: shelter.volunteer_info ?? "",
        volunteering_available: shelter.volunteering_available ?? false,
        dog_walking_available: shelter.dog_walking_available ?? false,
        dogs_available: shelter.dogs_available ?? true,
        cats_available: shelter.cats_available ?? true,
      });
      setTouched(new Set());
    }
  }, [shelter]);

  if (isLoading || !form || !shelter) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleSave = async () => {
    if (!shelter || !user) return;
    setSaving(true);
    try {
      const normalize = (v: unknown) =>
        typeof v === "string" ? (v.trim() || null) : (v === undefined ? null : v);

      const booleanKeys = new Set([
        "urgent_needs",
        "volunteering_available",
        "dog_walking_available",
        "dogs_available",
        "cats_available",
      ]);

      const changed: Record<string, any> = {};
      const originalValues: Record<string, any> = {};
      Object.entries(form).forEach(([key, value]) => {
        const newVal = normalize(value);
        const oldVal = normalize((shelter as any)[key]);
        if (newVal === oldVal) return;
        // For booleans, ignore phantom null->default diffs unless user actually toggled
        if (booleanKeys.has(key) && !touched.has(key)) return;
        changed[key] = newVal;
        originalValues[key] = (shelter as any)[key] ?? null;
      });

      if (Object.keys(changed).length === 0) {
        toast.info("Brak zmian do zapisu");
        setSaving(false);
        return;
      }

      const { error: updErr } = await supabase
        .from("shelters")
        .update(changed as any)
        .eq("id", shelter.id);
      if (updErr) throw updErr;

      const snapshotAfter = { ...shelter, ...changed };
      const { error: histErr } = await supabase.from("shelter_updates").insert({
        shelter_id: shelter.id,
        shelter_name: snapshotAfter.name,
        city: snapshotAfter.city,
        region: snapshotAfter.region ?? null,
        address: snapshotAfter.address ?? null,
        postal_code: snapshotAfter.postal_code ?? null,
        lat: snapshotAfter.lat,
        lng: snapshotAfter.lng,
        phone: snapshotAfter.phone ?? null,
        email: snapshotAfter.email ?? null,
        website: snapshotAfter.website ?? null,
        current_needs: snapshotAfter.current_needs ?? null,
        urgent_needs: snapshotAfter.urgent_needs ?? null,
        dog_walking_available: snapshotAfter.dog_walking_available ?? null,
        volunteering_available: snapshotAfter.volunteering_available ?? null,
        dogs_available: snapshotAfter.dogs_available ?? null,
        cats_available: snapshotAfter.cats_available ?? null,
        volunteer_info: snapshotAfter.volunteer_info ?? null,
        description: snapshotAfter.description ?? null,
        status: "approved",
        source: "owner_edit",
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        submitter_user_id: user.id,
        submitter_email: user.email ?? null,
        original_values: originalValues,
      } as any);
      if (histErr) {
        console.error("[EditNeeds] shelter_updates insert failed", histErr);
        toast.warning("Zapisano potrzeby, ale nie udało się zapisać wpisu w historii");
      } else {
        toast.success("Zapisano potrzeby");
      }
      queryClient.invalidateQueries({ queryKey: ["panel-shelter", slug] });
      queryClient.invalidateQueries({ queryKey: ["owned-shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-details", shelter.id] });
      queryClient.invalidateQueries({ queryKey: ["panel-history-full", shelter.id] });
    } catch (err: any) {
      toast.error(dbErrorMessage(err, "Nie udało się zapisać zmian"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-2.5 sm:space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{shelter.name}{shelter.city ? ` - ${shelter.city}` : ""}</span>
            {shelter.organization_verified && (
              <PanelBadge
                variant="verified"
                icon={BadgeCheck}
                tooltip="Profil potwierdzony przez przedstawiciela organizacji"
              >
                Zweryfikowany
              </PanelBadge>
            )}
          </div>
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Potrzeby</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Zaktualizuj listę potrzeb i informacje o wolontariacie i adopcjach.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground shrink-0 hidden sm:inline-flex">
          <a href={`https://gdzieschronisko.pl/schronisko/${shelter.slug}`} target="_blank" rel="noopener noreferrer">
            Zobacz publiczny profil
            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </a>
        </Button>
      </div>

      {/* AKTUALNE POTRZEBY */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Heart className="h-4 w-4" />
            </span>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm sm:text-base">Aktualne potrzeby</CardTitle>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  <Zap className="h-3 w-3" />
                  Zapis natychmiastowy
                </span>
              </div>
              <CardDescription className="text-xs sm:text-sm">Ta sekcja jest najczęściej oglądana przez darczyńców.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5 sm:space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="current_needs">Potrzeby (czego brakuje)</Label>
              <HelpHint label="Jak wypełnić potrzeby">
                <p className="font-semibold text-foreground">Lepiej krócej, konkretnie i częściej.</p>
                <p>Ludzie chętniej pomagają, gdy widzą świeżą datę aktualizacji i konkretne pozycje, np.:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>karma sucha Josera Adult 15 kg</li>
                  <li>koce, ręczniki, miski metalowe</li>
                  <li>żwirek bentonitowy</li>
                </ul>
                <p>Możesz też wkleić link do zbiórki (np. Zrzutka, Allegro lista życzeń) - darczyńcy łatwiej kupują, gdy mają gotowy link.</p>
                <p className="text-muted-foreground">Unikaj ogólników typu "wszystko się przyda".</p>
              </HelpHint>
            </div>
            <Textarea
              id="current_needs"
              rows={5}
              maxLength={1000}
              value={form.current_needs}
              onChange={(e) => setForm({ ...form, current_needs: e.target.value })}
              placeholder="np. karma sucha dla dorosłych psów, koce, miski..."
              className="resize-y"
            />
            <div className="text-xs text-muted-foreground tabular-nums text-right">
              {(form.current_needs ?? "").length} / 1000
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-orange-100 text-orange-600">
                <AlertTriangle className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Label className="font-medium">Pilne potrzeby</Label>
                  <HelpHint label="O pilnych potrzebach">
                    <p className="font-semibold text-foreground">Oznaczenie aktywne przez 30 dni.</p>
                    <p>Po włączeniu schronisko pojawia się z pomarańczową plakietką "Pilne" i jest wyróżnione w filtrach oraz na mapie.</p>
                    <p className="font-semibold text-foreground mt-2">Kiedy używać:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      <li>nagła sytuacja (przepełnienie, choroba, zima bez opału)</li>
                      <li>krytyczny brak konkretnego zasobu</li>
                      <li>akcja ratunkowa lub zbiórka z terminem</li>
                    </ul>
                    <p className="text-muted-foreground">Po 30 dniach oznaczenie automatycznie znika - jeśli sytuacja trwa, włącz ponownie.</p>
                  </HelpHint>
                </div>
                <p className="text-xs text-muted-foreground">Wyróżnij to schronisko jako pilnie potrzebujące pomocy.</p>
              </div>
            </div>
            <Switch
              checked={form.urgent_needs}
              onCheckedChange={(v) => { markTouched("urgent_needs"); setForm({ ...form, urgent_needs: v }); }}
            />
          </div>
        </CardContent>
      </Card>

      {/* WOLONTARIAT I ADOPCJE */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-4 w-4" />
            </span>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm sm:text-base">Wolontariat i adopcje</CardTitle>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  <Zap className="h-3 w-3" />
                  Zapis natychmiastowy
                </span>
              </div>
              <CardDescription className="text-xs sm:text-sm">Pokaż, jak wolontariusze mogą się zaangażować i które zwierzęta szukają domu.</CardDescription>

            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div className="flex items-center justify-between p-2.5 sm:p-3 border rounded-lg">
              <Label className="font-medium">Wolontariat dostępny</Label>
              <Switch
                checked={form.volunteering_available}
                onCheckedChange={(v) => {
                  markTouched("volunteering_available");
                  setForm((prev: any) => ({
                    ...prev,
                    volunteering_available: v,
                    ...(v ? {} : { volunteer_info: "", dog_walking_available: false }),
                  }));
                  if (!v) {
                    markTouched("volunteer_info");
                    markTouched("dog_walking_available");
                  }
                }}
              />
            </div>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-between p-2.5 sm:p-3 border rounded-lg">
                    <Label className={`font-medium ${!form.volunteering_available ? "text-muted-foreground" : ""}`}>Wyprowadzanie psów</Label>
                    <Switch
                      checked={form.dog_walking_available}
                      disabled={!form.volunteering_available}
                      onCheckedChange={(v) => { markTouched("dog_walking_available"); setForm({ ...form, dog_walking_available: v }); }}
                    />
                  </div>
                </TooltipTrigger>
                {!form.volunteering_available && (
                  <TooltipContent side="top">
                    Najpierw włącz „Wolontariat dostępny", aby zaznaczyć wyprowadzanie psów.
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center justify-between p-2.5 sm:p-3 border rounded-lg">
              <Label className="font-medium">Psy do adopcji</Label>
              <Switch
                checked={form.dogs_available}
                onCheckedChange={(v) => { markTouched("dogs_available"); setForm({ ...form, dogs_available: v }); }}
              />
            </div>
            <div className="flex items-center justify-between p-2.5 sm:p-3 border rounded-lg">
              <Label className="font-medium">Koty do adopcji</Label>
              <Switch
                checked={form.cats_available}
                onCheckedChange={(v) => { markTouched("cats_available"); setForm({ ...form, cats_available: v }); }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="volunteer_info" className={!form.volunteering_available ? "text-muted-foreground" : ""}>
                Informacje o wolontariacie
              </Label>
              <HelpHint label="Jak opisać wolontariat">
                <p className="font-semibold text-foreground">Konkrety pomagają zacząć.</p>
                <p>Napisz krótko, co wolontariusz powinien wiedzieć, np.:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>kiedy można przyjść (dni i godziny)</li>
                  <li>od jakiego wieku</li>
                  <li>czy potrzebne szkolenie / pierwsza wizyta</li>
                  <li>z kim się skontaktować</li>
                </ul>
                <p className="text-muted-foreground">Możesz dodać link do formularza zgłoszeniowego lub regulaminu.</p>
              </HelpHint>
            </div>
            {form.volunteering_available ? (
              <>
                <Textarea
                  id="volunteer_info"
                  rows={5}
                  maxLength={1000}
                  value={form.volunteer_info}
                  onChange={(e) => setForm({ ...form, volunteer_info: e.target.value })}
                  placeholder="Jak można pomóc? Wymagania, godziny, kontakt..."
                  className="resize-y"
                />
                <div className="text-xs text-muted-foreground tabular-nums text-right">
                  {(form.volunteer_info ?? "").length} / 1000
                </div>
              </>
            ) : (
              <div className="flex items-start gap-2.5 rounded-lg border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <p>Wolontariat jest oznaczony jako niedostępny - włącz przełącznik powyżej, aby dodać opis.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-stretch sm:justify-end sm:sticky sm:bottom-4 sm:pr-16 pb-2">
        <Button onClick={handleSave} disabled={saving} size="sm" className="w-full sm:w-auto sm:h-10 sm:px-4 shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Zapisz potrzeby
        </Button>
      </div>
    </div>
  );
};

export default EditNeeds;
